"use client";

import Player from "@vimeo/player";
import { Volume2, VolumeOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const InlineVideo = ({ src = "", vimeoLink = "", className = "", ...props }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const vimeoPlayerRef = useRef<Player | null>(null);
  const [isMuted, setIsMuted] = useState(true);

  // Auto-play/pause regular videos on scroll
  useEffect(() => {
    if (vimeoLink) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!videoRef.current) return;
          if (entry.isIntersecting) {
            videoRef.current.play();
          } else {
            videoRef.current.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    if (videoRef.current) observer.observe(videoRef.current);
    return () => {
      if (videoRef.current) observer.unobserve(videoRef.current);
    };
  }, [vimeoLink]);

  // Initialise Vimeo Player SDK once the iframe is mounted
  useEffect(() => {
    if (!vimeoLink || !iframeRef.current) return;

    const player = new Player(iframeRef.current);
    vimeoPlayerRef.current = player;

    return () => {
      player.destroy();
      vimeoPlayerRef.current = null;
    };
  }, [vimeoLink]);

  const toggleMute = async () => {
    if (vimeoPlayerRef.current) {
      await vimeoPlayerRef.current.setMuted(!isMuted);
      setIsMuted((m) => !m);
    } else if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted((m) => !m);
    }
  };

  const muteButton = (
    <button
      onClick={toggleMute}
      aria-label={isMuted ? "Unmute" : "Mute"}
      className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
    >
      {isMuted ? <VolumeOff size={18} /> : <Volume2 size={18} />}
    </button>
  );

  if (vimeoLink) {
    const vimeoId = vimeoLink.match(/vimeo\.com\/(\d+)/)?.[1] || vimeoLink;
    return (
      <div className="relative w-full flex items-center justify-start">
        <div className="w-full shrink-0" style={{ aspectRatio: "16/9" }}>
          <iframe
            ref={iframeRef}
            src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&loop=1&muted=1&background=1`}
            className={`${className} w-full h-full object-contain max-h-[80dvh] object-left`}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            {...props}
          />
        </div>
        {muteButton}
      </div>
    );
  }

  return (
    <div className="relative w-full flex items-center justify-start">
      <div className="w-full shrink-0">
        <video
          ref={videoRef}
          className={`${className} w-full object-contain max-h-[80dvh] object-left`}
          autoPlay
          loop
          muted={isMuted}
          playsInline
          src={src}
          {...props}
        />
        {muteButton}
      </div>
    </div>
  );
};
